/**
 * FromNumberPicker — one shared control for picking the outbound phone
 * number across every surface that sends a call or SMS.
 *
 * Presentational: the caller owns `useMyNumbers(contact)` (so it can also
 * read the current value for its dial/send action) and passes the full
 * accessible number list + the unified selection pair in. Selection is
 * shared app-wide by the hook, so picking here updates every other picker
 * on the page immediately.
 *
 * Each option shows small phone/SMS icons for the capabilities that number
 * supports — no icon means no support. Action buttons on the consuming
 * surface gate on the relevant capability.
 */
import { Phone, MessageSquare } from 'lucide-react';
import type { MyNumber } from '../../hooks/useMyNumbers';

interface FromNumberPickerProps {
  numbers: MyNumber[];
  value: string;
  onChange: (next: string) => void;
  loading: boolean;
  /** Accessible label; also rendered as the prefix. */
  label?: string;
  /** ARIA label for the <select>; describes what kind of number is chosen. */
  ariaLabel?: string;
  className?: string;
}

function capabilityBadge(num: MyNumber | undefined): string {
  if (!num) return '';
  const parts: string[] = [];
  if (num.voice_enabled) parts.push('📞');
  if (num.sms_enabled) parts.push('💬');
  return parts.length ? ' ' + parts.join('') : '';
}

export function FromNumberPicker({
  numbers,
  value,
  onChange,
  loading,
  label = 'From',
  ariaLabel = 'Outbound phone number',
  className,
}: FromNumberPickerProps) {
  const selected = numbers.find((n) => n.phone_number === value);

  return (
    <div
      className={
        'flex items-center gap-2 text-[0.6875rem] text-muted-foreground ' + (className ?? '')
      }
    >
      <span className="font-medium">{label}</span>
      {loading ? (
        <span className="opacity-60 italic">loading…</span>
      ) : numbers.length === 0 ? (
        <span className="text-destructive font-medium">no accessible numbers</span>
      ) : numbers.length === 1 ? (
        <span className="font-mono text-foreground inline-flex items-center gap-1">
          {numbers[0]!.phone_number}
          <CapabilityIcons num={numbers[0]!} />
          {numbers[0]!.friendly_name && (
            <span className="text-muted-foreground">· {numbers[0]!.friendly_name}</span>
          )}
        </span>
      ) : (
        <span className="inline-flex items-center gap-1.5">
          <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            aria-label={ariaLabel}
            className="px-2 py-1 rounded-md border border-border/60 bg-card text-[0.6875rem] text-foreground font-mono outline-none focus:border-primary/30 focus:ring-2 focus:ring-primary/10 cursor-pointer"
          >
            {numbers.map((n) => (
              <option key={n.id} value={n.phone_number}>
                {n.phone_number}
                {capabilityBadge(n)}
                {n.friendly_name ? ` · ${n.friendly_name}` : ''}
              </option>
            ))}
          </select>
          {selected && <CapabilityIcons num={selected} />}
        </span>
      )}
    </div>
  );
}

function CapabilityIcons({ num }: { num: MyNumber }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-hidden>
      {num.voice_enabled && (
        <Phone className="w-3 h-3 stroke-[2] text-brand-green" aria-label="supports calls" />
      )}
      {num.sms_enabled && (
        <MessageSquare className="w-3 h-3 stroke-[2] text-primary" aria-label="supports SMS" />
      )}
    </span>
  );
}
